import { Component, Prop, Vue } from 'vue-property-decorator';
import ClButtonBase from '../ClButtonBase/ClButtonBase.vue';
import { applyClasses } from '../../lib';
import { Classes } from '../ClAppBar/ClAppBar';

type ColorProp = 'default' | 'inherit' | 'primary' | 'secondary';
type VariantProp = 'flat' | 'contained' | 'outlined';
type EdgeProp = 'start' | 'end' | false;
type ClassesProp = {
  root: any;
  inner: any;
  edgeStart: any;
  edgeEnd: any;
  disabled: any;
};

@Component({
  components: {
    ClButtonBase
  }
})
export default class ClIconButton extends ClButtonBase {
  /**
   * The color of the component. It supports those theme colors that make sense for this component.
   */
  @Prop({
    type: String,
    default: 'default',
    validator: (x: ColorProp) =>
      [
        'default',
        'inherit',
        'primary',
        'secondary',
        'error',
        'warning',
        'success'
      ].includes(x)
  })
  readonly color!: ColorProp;

  @Prop({
    type: Object as () => ClassesProp,
    default: () => ({
      root: '',
      inner: '',
      edgeEnd: '',
      edgeStart: ''
    })
  })
  readonly classes!: Classes;

  /**
   * If true, the base button will be disabled.
   */
  @Prop(Boolean)
  readonly disabled!: boolean;

  /**
   * Uses a negative margin to counteract the padding on one
   * side (this is often helpful for aligning the left or right
   * side of the icon with content above or below, without ruining the border
   * size and shape).
   */
  @Prop({
    type: [String, Boolean],
    default: false,
    validator: (x: EdgeProp) => ['start', 'end', false].includes(x)
  })
  readonly edge!: EdgeProp;

  /**
   * The variant of the button
   */
  @Prop({
    type: String,
    default: 'flat',
    validator: (x: VariantProp) => ['flat', 'contained', 'outlined'].includes(x)
  })
  readonly variant!: VariantProp;

  get rootClassName(): object {
    return applyClasses(
      {
        'cl-icon-button': true,
        [`cl-icon-button--color-${this.color}`]: true,
        [`cl-icon-button--variant-${this.variant}`]: true,
        [`cl-icon-button--disabled`]: this.disabled,
        [`cl-icon-button--edge-${this.edge}`]: Boolean(this.edge)
      },
      this.classes.root,
      this.disabled && this.classes.disabled,
      this.edge === 'start' && this.classes.edgeStart,
      this.edge === 'end' && this.classes.edgeEnd
    );
  }

  get innerClassName(): object {
    return applyClasses('cl-icon-button__inner', this.classes.inner);
  }
}
